import { Box, Button, ButtonGroup, Stack, Typography } from "@mui/material"
import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { Locker } from "shared/locker"
import { TemperatureDemoChart } from "./TemperatureDemoChart"
import {
  CRIT_COLOR,
  formatClock,
  formatDuration,
  formatTemperature,
  formatTemperatureValue,
  LEVEL_COLORS,
  LEVEL_LABEL_KEYS,
  WARN_COLOR,
} from "./temperatureDemoFormat"
import {
  latestBreaches,
  TemperatureRange,
  temperatureSeries,
} from "./temperatureDemoGenerator"
import {
  activeTemperatureDemoDoors,
  TemperatureDemoScenario,
} from "./temperatureDemoTypes"
import { useDemoNow } from "./useDemoNow"

const RANGES: { range: TemperatureRange; labelKey: string }[] = [
  { range: "6h", labelKey: "temperatureDemo.range6h" },
  { range: "24h", labelKey: "temperatureDemo.range24h" },
  { range: "7d", labelKey: "temperatureDemo.range7d" },
]

const eyebrowSx = {
  fontSize: "0.66rem",
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase" as const,
  color: "text.secondary",
}

type Props = {
  locker: Locker
  /** The sst2 door id (`door.door_id` in the drawer). */
  doorId: string
  doorLabel: string
}

/**
 * LOCKER-534 — read-only "Température · DÉMO" section of the door drawer.
 * Renders nothing unless the locker's demo is on AND this door is in its map.
 * No scenario control here by design: the setup dialog owns that.
 */
export const DoorTemperatureSection: FC<Props> = ({ locker, doorId, doorLabel }) => {
  const scenario = activeTemperatureDemoDoors(locker)[String(doorId)]
  if (!scenario) return null
  return (
    <DoorTemperatureSectionBody
      doorId={String(doorId)}
      doorLabel={doorLabel}
      scenario={scenario}
    />
  )
}

const DoorTemperatureSectionBody: FC<{
  doorId: string
  doorLabel: string
  scenario: TemperatureDemoScenario
}> = ({ doorId, doorLabel, scenario }) => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const now = useDemoNow()
  const [range, setRange] = useState<TemperatureRange>("24h")
  const series = useMemo(
    () => temperatureSeries(doorId, scenario, range, now),
    [doorId, scenario, range, now],
  )
  const level = LEVEL_COLORS[series.currentLevel]
  const withDay = range === "7d"
  const events = latestBreaches(series)

  const stats = [
    { k: "temperatureDemo.statCurrent", v: formatTemperature(series.current, lang) },
    { k: "temperatureDemo.statMin", v: formatTemperature(series.min, lang) },
    { k: "temperatureDemo.statMax", v: formatTemperature(series.max, lang) },
    { k: "temperatureDemo.statOutOfRange", v: formatDuration(series.outOfRangeMinutes) },
  ]

  return (
    <Stack
      component="section"
      data-ui="UI-SL2W-291"
      aria-label={t("temperatureDemo.sectionTitle")}
      spacing={1.75}
      sx={{ py: 2 }}
    >
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
        <Typography sx={eyebrowSx}>{t("temperatureDemo.sectionTitle")}</Typography>
        <Box
          component="span"
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: ".08em",
            px: "6px",
            py: "2px",
            borderRadius: 1,
            border: "1px dashed rgba(35,66,133,.55)",
            color: "primary.main",
            bgcolor: "rgba(35,66,133,.04)",
          }}
        >
          {t("temperatureDemo.demoBadge")}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <ButtonGroup
          size="small"
          role="group"
          aria-label={t("temperatureDemo.rangeGroup")}
        >
          {RANGES.map((r) => (
            <Button
              key={r.range}
              aria-pressed={range === r.range}
              variant={range === r.range ? "contained" : "outlined"}
              onClick={() => setRange(r.range)}
              sx={{ fontSize: 12, fontWeight: 600, textTransform: "none", minWidth: 0, px: 1.25 }}
            >
              {t(r.labelKey)}
            </Button>
          ))}
        </ButtonGroup>
      </Stack>

      <Stack direction="row" alignItems="flex-end" spacing={1.75} flexWrap="wrap">
        <Typography
          data-testid="temperature-current"
          sx={{
            fontSize: 36,
            fontWeight: 600,
            letterSpacing: "-0.02em",
            lineHeight: 1,
            fontVariantNumeric: "tabular-nums",
            color: level.text,
          }}
        >
          {formatTemperatureValue(series.current, lang)}
          <Box component="span" sx={{ fontSize: 16, fontWeight: 600, ml: "2px" }}>
            °C
          </Box>
        </Typography>
        <Box
          component="span"
          sx={{
            fontSize: 12,
            fontWeight: 600,
            px: 1,
            py: 0.5,
            borderRadius: 1.5,
            mb: 0.5,
            bgcolor: level.chipBg,
            color: level.chipFg,
          }}
        >
          {t(LEVEL_LABEL_KEYS[series.currentLevel])}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Typography sx={{ fontSize: 12, color: "text.secondary", pb: 0.75 }}>
          {t("temperatureDemo.targetRange")}
        </Typography>
      </Stack>

      <TemperatureDemoChart
        series={series}
        now={now}
        ariaLabel={t("temperatureDemo.chartAria", {
          door: doorLabel,
          current: formatTemperature(series.current, lang),
          min: formatTemperatureValue(series.min, lang),
          max: formatTemperatureValue(series.max, lang),
        })}
      />

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1 }}>
        {stats.map((s) => (
          <Stack
            key={s.k}
            spacing={0.25}
            sx={{ border: "1px solid rgba(0,0,0,.08)", borderRadius: 2.5, px: 1.5, py: 1.25 }}
          >
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{t(s.k)}</Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
              {s.v}
            </Typography>
          </Stack>
        ))}
      </Box>

      <Stack spacing={0.75}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          {t("temperatureDemo.breachesTitle")}
        </Typography>
        {events.length === 0 && (
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {t("temperatureDemo.noBreaches")}
          </Typography>
        )}
        {events.map((e) => (
          <Stack
            key={e.startMinutesAgo}
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{
              px: 1.25,
              py: 1,
              borderRadius: 2,
              bgcolor: "#fafbfd",
              border: "1px solid rgba(0,0,0,.06)",
              fontSize: 12,
            }}
          >
            <Box
              component="span"
              aria-hidden
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                flexShrink: 0,
                bgcolor: e.critical ? CRIT_COLOR : WARN_COLOR,
              }}
            />
            <Box component="span" sx={{ fontWeight: 600, minWidth: 92 }}>
              {t(e.critical ? "temperatureDemo.breachCritical" : "temperatureDemo.breachWarning")}
            </Box>
            <Box component="span" sx={{ flexGrow: 1, fontVariantNumeric: "tabular-nums" }}>
              {`${formatClock(now, e.startMinutesAgo, lang, withDay)} → ${
                e.ongoing
                  ? t("temperatureDemo.ongoing")
                  : formatClock(now, e.endMinutesAgo, lang, withDay)
              }`}
            </Box>
            <Box component="span" sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
              {`${t(e.high ? "temperatureDemo.peakMax" : "temperatureDemo.peakMin", {
                value: formatTemperature(e.peak, lang),
              })} · ${formatDuration(e.durationMinutes)}`}
            </Box>
          </Stack>
        ))}
      </Stack>

      <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.5 }}>
        {t("temperatureDemo.footnote")}
      </Typography>
    </Stack>
  )
}
